import { FunctionComponent, useEffect } from 'react'
import moment from 'moment-timezone'
import { Daycare, DaycareType } from '@/types/Daycare'
import styles from './export.module.scss'
import profileStyles from '../DaycareProfileHandler.module.scss'
import Title from '@/components/_custom/Title/title'
import { useTranslation } from 'react-i18next'
import ExportParents from '@/components/Export/ParentsExport/ParentsExport'
import ExportAbsences from '@/components/Export/AbsencesExport/AbsencesExport'
import ExportRegularSlots from '@/components/Export/RegularSlotsExport/RegularSlotsExport'
import ExportOccasionalSlots from '@/components/Export/OccasionalSlotsExport/OccasionalSlotsExport'
import ExportStaffs from '@/components/Export/StaffsExport/StaffsExport'
import ExportWorks from '@/components/Export/WorksExport/WorksExport'
import { getTextFromLanguage } from '@/modules/helper/languageHelper'
import ExportTaskNotes from '@/components/Export/TaskNotesExport/TaskNotesExport.tsx'
import ExportChilds from '@/components/Export/ChildsExport/ChildsExport.tsx'
import ExportEvents from '@/components/Export/EventsExport/EventsExport.tsx'
import ExportReprintReport from '@/components/Export/ReprintReport/ReprintReport.tsx'
import { useAuth } from '@/modules/context/AuthContext.tsx'

interface ExportProps {
    daycare: Daycare
}

const DaycareExport: FunctionComponent<ExportProps> = ({ daycare }: ExportProps) => {
    const { user } = useAuth()

    const { t } = useTranslation()

    const minDate: string = daycare.created
        ? moment(daycare.created, 'DD-MM-YYYY').format('DD-MM-YYYY')
        : moment().format('DD-MM-YYYY')

    const isRpeOrLaep: boolean = daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep

    useEffect(() => {
        moment().locale(getTextFromLanguage())
    }, [])

    return (
        <div className={profileStyles.PagePosition}>
            {(user?.currentDaycareRights.canReadOccasionalSlot ||
                user?.currentDaycareRights.canReadFamilies ||
                user?.currentDaycareRights.canReadContracts) && (
                <div className={styles.ExportPlacement}>
                    <Title
                        text={t('myExport') + ' ' + t(isRpeOrLaep ? 'public' : 'family').toLowerCase()}
                        className={styles.Title}
                    />
                    {user.currentDaycareRights.canReadOccasionalSlot && (
                        <div className={styles.CaseContainer}>
                            <ExportOccasionalSlots daycares={[daycare]} minDate={minDate} isRpeOrLaep={isRpeOrLaep} />
                        </div>
                    )}
                    {user.currentDaycareRights.canReadContracts && (
                        <div className={styles.CaseContainer}>
                            <ExportRegularSlots daycares={[daycare]} minDate={minDate} isRpeOrLaep={isRpeOrLaep} />
                            <ExportAbsences daycares={[daycare]} minDate={minDate} isRpeOrLaep={isRpeOrLaep} />
                        </div>
                    )}
                    {user?.currentDaycareRights.canReadFamilies && (
                        <div className={styles.CaseContainer}>
                            <ExportParents daycares={[daycare]} isRpeOrLaep={isRpeOrLaep} />
                            <ExportChilds daycares={[daycare]} isRpeOrLaep={isRpeOrLaep} />
                        </div>
                    )}
                </div>
            )}
            {(user?.currentDaycareRights.canReadEvents ||
                user?.currentDaycareRights.canReadTaskNote ||
                user?.currentDaycareRights.canReadParamsDaycare) && (
                <div className={styles.ExportPlacement}>
                    <Title text={t('myExport') + ' ' + t('structure').toLowerCase()} className={styles.Title} />
                    {(user?.currentDaycareRights.canReadEvents || user?.currentDaycareRights.canReadTaskNote) && (
                        <div className={styles.CaseContainer}>
                            {user?.currentDaycareRights.canReadTaskNote && (
                                <ExportTaskNotes daycares={[daycare]} minDate={minDate} isRpeOrLaep={isRpeOrLaep} />
                            )}
                            {user?.currentDaycareRights.canReadEvents && (
                                <ExportEvents daycares={[daycare]} minDate={minDate} isRpeOrLaep={isRpeOrLaep} />
                            )}
                        </div>
                    )}
                    {user?.currentDaycareRights.canReadParamsDaycare && (
                        <div className={styles.CaseContainer}>
                            <ExportReprintReport daycares={[daycare]} minDate={minDate} fromGroup={false} />
                        </div>
                    )}
                </div>
            )}
            {daycare.staffAccess &&
                (user?.currentDaycareRights.canReadStaffRights || user?.currentDaycareRights.canReadStaffWorks) && (
                    <div className={styles.ExportPlacement}>
                        <Title text={t('myExport') + ' ' + t('staff').toLowerCase()} className={styles.Title} />
                        <div className={styles.CaseContainer}>
                            {user?.currentDaycareRights.canReadStaffRights && <ExportStaffs daycares={[daycare]} />}
                            {user?.currentDaycareRights.canReadStaffWorks && (
                                <ExportWorks daycares={[daycare]} minDate={minDate} />
                            )}
                        </div>
                    </div>
                )}
        </div>
    )
}

export default DaycareExport
